import {useLayoutEffect, useRef} from 'react';
import type {RefObject} from 'react';

type UseAssignFocusOptions = {
  disabled?: boolean;
  delay?: number;
};

/**
 * Declaratively assign focus to a given element when it mounts, or when `disabled` is false.
 * Primarily useful when needing to assign focus upon UI change for accessibility purposes.
 *
 * Optionally, you can pass a `delay` to delay the focus assignment. This can be useful to e.g. account for transitions between elements.
 *
 * When assigning focus, make sure the target element has a `tabIndex`. Most commonly this should have a value of `-1`.
 * This enables setting focus in code, but doesn't add it to the focus order when navigating with the keyboard.
 *
 * @example
 * ```tsx
 * const {focusRef} = useAssignFocus<HTMLButtonElement>();
 *
 * return <button ref={focusRef} tabIndex={-1}>Focus me</button>;
 * ```
 */
export function useAssignFocus<T extends HTMLElement>(
  options?: UseAssignFocusOptions
): {focusRef: RefObject<T>} {
  const {disabled = false, delay} = options ?? {};
  const ref = useRef<T>(null);

  useLayoutEffect(() => {
    const focusElement = () => {
      try {
        if (!disabled && ref?.current) {
          ref.current.focus();
        }
      } catch {
        // silently fail
      }
    };

    if (delay !== undefined) {
      const timeoutId = setTimeout(focusElement, delay);
      return () => clearTimeout(timeoutId);
    } else {
      focusElement();
      return undefined;
    }
  }, [disabled, delay]);

  return {focusRef: ref};
}
